import { forwardRef } from 'react';
import { Typography, TypographyProps } from '../Typography';

/**
 * Body text component for standard body text content.
 *
 * This is a convenience wrapper around Typography with body1/body2 variants.
 * - body1: Small body text
 * - body2: Standard body text
 *
 * @example
 * ```tsx
 * <Body>Standard body text (defaults to body1)</Body>
 * <Body variant="body2">Larger body text</Body>
 * <Body color="text.secondary">Muted body text</Body>
 * ```
 */
export interface BodyProps extends Omit<TypographyProps, 'variant'> {
  /**
   * Body variant - controls the size of the text
   * @default 'body1'
   */
  variant?: 'body1' | 'body2';
}

export const Body = forwardRef<HTMLElement, BodyProps>(({ variant = 'body1', ...props }, ref) => (
  <Typography ref={ref} variant={variant} {...props} />
));

Body.displayName = 'Body';

/**
 * Caption text component for small secondary text, labels, and annotations.
 *
 * This is a convenience wrapper around Typography with variant="caption".
 * Use for image captions, form field hints, or small labels.
 *
 * @example
 * ```tsx
 * <Caption>Image caption</Caption>
 * <Caption color="text.secondary">Helper text</Caption>
 * ```
 */
export type CaptionProps = Omit<TypographyProps, 'variant'>;

export const Caption = forwardRef<HTMLElement, CaptionProps>((props, ref) => (
  <Typography ref={ref} variant="caption" {...props} />
));

Caption.displayName = 'Caption';

/**
 * Subtitle1 component for section subtitles and secondary headings.
 *
 * This is a convenience wrapper around Typography with variant="subtitle1".
 * Use for section subtitles or emphasized secondary text.
 *
 * @example
 * ```tsx
 * <Subtitle1>Section Subtitle</Subtitle1>
 * <Subtitle1 color="primary" gutterBottom>Emphasized Subtitle</Subtitle1>
 * ```
 */
export type Subtitle1Props = Omit<TypographyProps, 'variant'>;

export const Subtitle1 = forwardRef<HTMLElement, Subtitle1Props>((props, ref) => (
  <Typography ref={ref} variant="subtitle1" {...props} />
));

Subtitle1.displayName = 'Subtitle1';

/**
 * Subtitle2 component for smaller subtitles and secondary headings.
 *
 * This is a convenience wrapper around Typography with variant="subtitle2".
 * Use for smaller section subtitles or secondary text.
 *
 * @example
 * ```tsx
 * <Subtitle2>Small Subtitle</Subtitle2>
 * <Subtitle2 color="text.secondary">Muted Subtitle</Subtitle2>
 * ```
 */
export type Subtitle2Props = Omit<TypographyProps, 'variant'>;

export const Subtitle2 = forwardRef<HTMLElement, Subtitle2Props>((props, ref) => (
  <Typography ref={ref} variant="subtitle2" {...props} />
));

Subtitle2.displayName = 'Subtitle2';
